import { ReactNode, useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../store";
import { ListTreeViewToggle } from "../../../../common/components/ListTreeViewToggle/ListTreeViewToggle";
import { IconSortFilterV2 } from "../../../../common/icons/IconSortFilterV2/IconSortFilterV2";
import { IconSortUpDown } from "../../../../common/icons/IconSortUpDown/IconSortUpDown";
import { KpiSelectorModal, KpiSelectorProps } from "../../../../common/components/KpiSelectorModal/KpiSelectorModal";
import { SortModal } from "../SortModal/SortModal";
import type { SortModalColumn, SortDirection } from "../SortModal/SortModal";
import {
  SearchSelect,
  SearchCallback,
  SelectionCallback,
} from "../../../../common/components/SearchSelect/SearchSelect";
import { IconDownloadButton } from "../../../../common/icons/IconDownloadButton/IconDownloadButton";
import { IconRotatingCircle } from "../../../../common/icons/IconRotatingCircle/IconRotatingCircle";
import { ExportConfig } from "../Header/ActionReportHeader";
import { useCurrentViewExportV2Mutation, useGetExportListV2Query } from "../../../../services/ExportV2/exportV2API";
import { ExportDetails, TextOverride } from "../../../../services/ExportV2/exportV2API.types";
import { STATUS_DONE, STATUS_IN_PROGRESS, STATUS_PENDING } from "../../../../common/constants/export-constants";
import { isExportDisabledBySessionSsoOrigin, buildExportFields } from "../../../../common/util/exportUtil";
import { ActionReportExportModal } from "../../../ServerSideExport/ActionReportExportModal/ActionReportExportModal";
import { ActionReportExportConfirmation } from "../../../ServerSideExport/ActionReportExportModal/ActionReportExportConfirmation";
import { dispatchNotifyInProgressEvent } from "../../../ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import { LeafBannerToast } from "../../../../common/leaf/LeafBannerToast/LeafBannerToast";
import { storedLocale } from "../../../Authentication/util";
import defaultContent from "../../../../content/actionReports";
import serverSideExportContent from "../../../../content/serverSideExport";
import "./ActionReportToolbar.scss";

export interface TreeViewToggleConfig {
  enabled: boolean;
  isTreeView: boolean;
  onToggle: () => void;
  disabled?: boolean;
  label: string;
}

export interface SortConfig {
  enabled: boolean;
  direction: "asc" | "desc";
  onClick: () => void;
  disabled?: boolean;
}

export interface SortModalConfig {
  enabled: boolean;
  columns: SortModalColumn[];
  columnSortState: Record<string, SortDirection | undefined>;
  onApply: (kpi: string, direction: SortDirection) => void;
  disabled?: boolean;
}

export interface SearchConfig {
  enabled: boolean;
  searchCallback: SearchCallback;
  selectionCallback: SelectionCallback;
  searchText: string;
  clearFunction: () => void;
  disabled: boolean;
  placeholder?: string;
  onFocus?: () => void;
}

export interface ActionReportToolbarProps {
  // Group Leader Filter slot (will be filled by ABOT-12323)
  groupLeaderFilterSlot?: ReactNode;

  // List/Tree View Toggle
  treeViewToggle?: TreeViewToggleConfig;

  // Sort
  sort?: SortConfig;

  // Sort Modal (mobile bottom sheet)
  sortModal?: SortModalConfig;
  sortModalTrack?: () => void;

  // KPI Selector
  kpiSelector?: KpiSelectorProps;

  // Search
  search?: SearchConfig;

  // Export
  export?: ExportConfig;
  exportTrack?: () => void;

  // Report metadata
  reportLength: number;
  isAllEnriched?: boolean;

  // Export list for button state
  exportList?: any[];
}

export const ActionReportToolbar = ({
  groupLeaderFilterSlot,
  treeViewToggle,
  sort,
  sortModal,
  sortModalTrack = () => {},
  kpiSelector,
  search,
  export: exportConfig,
  exportTrack = () => {},
  reportLength,
  isAllEnriched = true,
  exportList = [],
}: ActionReportToolbarProps) => {
  const { t } = useTranslation(["actionReports", "serverSideExport"]);
  const [showSortModal, setShowSortModal] = useState(false);
  const sortModalTriggerRef = useRef<HTMLButtonElement>(null);
  const hasActiveSort = Object.values(sortModal?.columnSortState ?? {}).some(Boolean);
  const boot = useAppSelector((state) => state.boot);
  const { configuration, user } = boot;
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { profile } = configuration;

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isExportListModalOpen, setIsExportListModalOpen] = useState(false);
  const [showExportError, setShowExportError] = useState(false);

  const { los: isExportDisabledBySsoOrigin } = isExportDisabledBySessionSsoOrigin();
  const isExportDisabled = isExportDisabledBySsoOrigin || !!configuration.actionReports?.disableExport;
  const shouldShowExport = exportConfig && !isExportDisabled;
  const skipExportQuery = !exportConfig?.moduleId || isExportDisabled;

  const [currentViewExport] = useCurrentViewExportV2Mutation();

  const { data: exportListData } = useGetExportListV2Query(
    { moduleId: exportConfig?.moduleId ?? "" },
    { skip: skipExportQuery },
  );

  const exportListFromQuery = exportListData?.Exports ?? [];
  const hasExistingExports = exportListFromQuery.length > 0;
  const exportButtonText = t("exportButton", defaultContent.exportButton);

  const hasPendingOrAvailableExports = exportListFromQuery.some(
    ({ ExportStatus }) =>
      ExportStatus === STATUS_PENDING || ExportStatus === STATUS_IN_PROGRESS || ExportStatus === STATUS_DONE,
  );

  const userAff = user.aff;
  const userAbo = user.abo;
  const userIsoCountryCode = user.isoCountryCode;

  const triggerExport = useCallback(async () => {
    if (!exportConfig || isExportDisabled) return;

    // Use custom export trigger if provided (e.g., SAQ export with specific metadata)
    if (exportConfig.customTriggerExport) {
      try {
        await exportConfig.customTriggerExport();
        dispatchNotifyInProgressEvent();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Failed to trigger export for module "${exportConfig.moduleId}":`, error);
        setShowExportError(true);
      }
      return;
    }

    const locale = storedLocale();
    const isoCountryCode = userIsoCountryCode || "";
    const affAbo = `${userAff}-${userAbo}`;
    const { address, addressLinesDivider } = profile?.format || {};

    const textOverride = t("textOverride", {
      returnObjects: true,
      defaultValue: serverSideExportContent.textOverride,
      ns: "serverSideExport",
    }) as TextOverride;

    const exportFields = t("exportFields", {
      returnObjects: true,
      defaultValue: serverSideExportContent.exportFields,
      ns: "serverSideExport",
    }) as Record<string, string>;

    const fields = buildExportFields(exportConfig.selectedKpis, exportFields, exportConfig.fieldTitleOverrides);

    const exportPayload = {
      moduleId: exportConfig.moduleId,
      reportAffAbo: affAbo,
      period: selectedPeriod,
      startedBy: exportConfig.exportType || "",
      locale,
      textOverride,
      fields,
      countryCode: isoCountryCode,
      format: {
        address: {
          pattern: address || "",
          addressLineDivider: addressLinesDivider || "",
        },
      },
      affAbos: exportConfig.exportAffAbos,
      filteredByKPIS: exportConfig.selectedKpis.length > 0,
    };

    try {
      await currentViewExport(exportPayload);
      dispatchNotifyInProgressEvent();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to trigger export for module "${exportConfig.moduleId}":`, error);
      setShowExportError(true);
    }
  }, [
    exportConfig,
    isExportDisabled,
    profile,
    userAff,
    userAbo,
    userIsoCountryCode,
    selectedPeriod,
    t,
    currentViewExport,
  ]);

  const handleExportButtonClick = () => {
    exportTrack();
    if (exportConfig?.confirmationMessage && !hasPendingOrAvailableExports) {
      setIsConfirmationModalOpen(true);
    } else {
      setIsExportListModalOpen(true);
    }
  };

  const handleConfirmationCancel = () => {
    setIsConfirmationModalOpen(false);
  };

  const handleConfirmationConfirm = () => {
    setIsConfirmationModalOpen(false);
    triggerExport();
  };

  const handleExportModalClose = () => {
    setIsExportListModalOpen(false);
  };

  const handleOpenExportModalEvent = useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.moduleId === exportConfig?.moduleId && customEvent.detail?.open) {
        setIsExportListModalOpen(true);
      }
    },
    [exportConfig?.moduleId],
  );

  useEffect(() => {
    window.addEventListener("OpenExportV2Modal", handleOpenExportModalEvent);
    return () => {
      window.removeEventListener("OpenExportV2Modal", handleOpenExportModalEvent);
    };
  }, [handleOpenExportModalEvent]);

  const exportDisabled = (reportLength === 0 && !hasExistingExports) || !isAllEnriched || isExportDisabled;

  const showInProgress = exportListFromQuery.some(
    ({ ExportStatus }) => ExportStatus === STATUS_PENDING || ExportStatus === STATUS_IN_PROGRESS,
  );
  const showDone = exportListFromQuery.some(({ ExportStatus }) => ExportStatus === STATUS_DONE);

  return (
    <div className="ActionReportToolbar">
      {showExportError && (
        <LeafBannerToast theme="red" show={showExportError} closeFunction={() => setShowExportError(false)}>
          <span>{t("exportError", defaultContent.exportError)}</span>
        </LeafBannerToast>
      )}
      {groupLeaderFilterSlot && <div className="ActionReportToolbar__groupLeaderSlot">{groupLeaderFilterSlot}</div>}

      <div className="ActionReportToolbar__buttons">
        {/* List/Tree View Toggle */}
        {treeViewToggle?.enabled && (
          <div className="ActionReportToolbar__treeViewToggle">
            <ListTreeViewToggle
              isTreeView={treeViewToggle.isTreeView}
              onToggle={treeViewToggle.onToggle}
              disabled={treeViewToggle.disabled && !treeViewToggle.isTreeView}
              label={treeViewToggle.label}
            />
          </div>
        )}

        {/* Sort Button */}
        {sort?.enabled && (
          <div className="ActionReportToolbar__sortButtonGroup">
            <button
              type="button"
              className={`ActionReportToolbar__sortButton ActionReportToolbar__sortButton--left ${
                sort.direction === "asc" ? "ActionReportToolbar__sortButton--active" : ""
              }`}
              onClick={sort.onClick}
              disabled={sort.disabled}
              aria-label="Sort ascending"
              aria-pressed={sort.direction === "asc"}
              data-testid="sort-ascending"
            >
              <IconSortFilterV2
                direction="asc"
                disabled={sort.disabled}
                variant={sort.disabled ? "indigo" : sort.direction === "asc" ? "indigo" : "default"}
                width="1.4rem"
                height="1.4rem"
              />
            </button>
            <button
              type="button"
              className={`ActionReportToolbar__sortButton ActionReportToolbar__sortButton--right ${
                sort.direction === "desc" ? "ActionReportToolbar__sortButton--active" : ""
              }`}
              onClick={sort.onClick}
              disabled={sort.disabled}
              aria-label="Sort descending"
              aria-pressed={sort.direction === "desc"}
              data-testid="sort-descending"
            >
              <IconSortFilterV2
                direction="desc"
                disabled={sort.disabled}
                variant={sort.disabled ? "disabled" : sort.direction === "desc" ? "indigo" : "default"}
                width="1.4rem"
                height="1.4rem"
              />
            </button>
          </div>
        )}

        {/* Sort Modal trigger */}
        {sortModal?.enabled && (
          <div className="ActionReportToolbar__sortModalTrigger">
            <button
              type="button"
              ref={sortModalTriggerRef}
              className={`ActionReportToolbar__sortModalButton${
                showSortModal || hasActiveSort ? " ActionReportToolbar__sortModalButton--active" : ""
              }`}
              onClick={() =>
                setShowSortModal((prev) => {
                  const next = !prev;
                  if (next) sortModalTrack();
                  return next;
                })
              }
              disabled={sortModal.disabled}
              aria-label={t("sortButtonAriaLabel", defaultContent.sortButtonAriaLabel)}
              data-testid="sort-modal-trigger"
            >
              <IconSortUpDown width="1.125rem" height="1.125rem" />
            </button>
            {hasActiveSort && !sortModal.disabled && (
              <span className="ActionReportToolbar__sortModalBadge" data-testid="sort-modal-badge">
                1
              </span>
            )}
            <SortModal
              show={showSortModal}
              onClose={() => setShowSortModal(false)}
              columns={sortModal.columns}
              columnSortState={sortModal.columnSortState}
              onApply={sortModal.onApply}
              triggerRef={sortModalTriggerRef}
            />
          </div>
        )}

        {/* KPI Selector */}
        {kpiSelector && (
          <div className="ActionReportToolbar__kpiSelector">
            <KpiSelectorModal {...kpiSelector} />
          </div>
        )}
      </div>

      {/* Search Bar */}
      {search?.enabled && (
        <div
          className="ActionReportToolbar__search"
          data-testid="action-report-search-icon"
          onFocusCapture={search.onFocus}
        >
          <SearchSelect
            placeholderText={search.placeholder || t("searchPlaceholder", defaultContent.searchPlaceholder)}
            clearSearchAriaLabel={t("clearSearch", defaultContent.clearSearch)}
            searchCallback={search.searchCallback}
            selectionCallback={search.selectionCallback}
            disabled={search.disabled}
            searchText={search.searchText}
            clear={search.clearFunction}
          />
        </div>
      )}

      {/* Export Button */}
      {shouldShowExport && (
        <button
          type="button"
          className={`ActionReportToolbar__exportButton ${
            exportDisabled ? "ActionReportToolbar__exportButton--disabled" : ""
          }`}
          onClick={handleExportButtonClick}
          disabled={exportDisabled}
          aria-label="Export report"
          data-testid="export-button"
        >
          <span className="ActionReportToolbar__exportButton__iconWrapper">
            {showInProgress ? <IconRotatingCircle /> : <IconDownloadButton width="1.125rem" height="1.125rem" />}
          </span>
          {showDone && !showInProgress && !exportDisabled && (
            <span
              className="ActionReportToolbar__exportButton__notificationDot"
              data-testid="export-notification-dot"
            />
          )}
        </button>
      )}

      {shouldShowExport && (
        <>
          <ActionReportExportConfirmation
            open={isConfirmationModalOpen}
            onCancel={handleConfirmationCancel}
            onConfirm={handleConfirmationConfirm}
            reportDescription={exportConfig.confirmationMessage ?? ""}
          />
          <ActionReportExportModal
            moduleId={exportConfig.moduleId}
            selectedKpis={exportConfig.selectedKpis}
            affAbos={exportConfig.exportAffAbos}
            startedBy={exportConfig.exportType || ""}
            startedByLabels={exportConfig.startedByLabels}
            fieldTitleOverrides={exportConfig.fieldTitleOverrides}
            open={isExportListModalOpen}
            onCloseCallbackFn={handleExportModalClose}
            customTriggerExport={exportConfig.customTriggerExport}
          />
        </>
      )}
    </div>
  );
};
